import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Truck, CreditCard, Sparkles } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, formatRON, computeDiscount } from "../lib/shop";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

const SHIPPING = 19.99;

export default function Checkout() {
  const { items, subtotal, grossSubtotal, lineDiscountTotal, clear } = useCart();
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const cancelled = new URLSearchParams(loc.search).get("cancelled");
  const discount = computeDiscount(subtotal);

  const [promoInput, setPromoInput] = useState("");
  const [promoCode, setPromoCode] = useState(null);
  const [promoErr, setPromoErr] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);

  const promoAmount = promoCode ? Math.round(subtotal * promoCode.percent) / 100 : 0;
  const effectiveOrderDiscount = promoCode ? 0 : discount.amount;

  const [form, setForm] = useState({
    full_name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    address: "",
    city: "",
    county: "",
    postal_code: "",
    notes: "",
  });
  const [method, setMethod] = useState("cod");
  const [loading, setLoading] = useState(false);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const total = subtotal - effectiveOrderDiscount - promoAmount + (items.length ? SHIPPING : 0);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoChecking(true); setPromoErr("");
    try {
      const { data } = await api.get(`/promo/validate/${code}`);
      if (data.valid) {
        setPromoCode({ code: data.code, percent: data.percent });
        toast.success(`${data.code} ${t("checkout.promo.applied")} ${data.percent}% ${t("checkout.promo.discount")}`);
      } else {
        setPromoErr(data.reason || t("checkout.promo.invalid"));
        setPromoCode(null);
      }
    } catch {
      setPromoErr(t("checkout.promo.error"));
    } finally { setPromoChecking(false); }
  };

  const removePromo = () => { setPromoCode(null); setPromoInput(""); setPromoErr(""); };

  if (!items.length) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h2 className="font-display text-3xl font-black tracking-tighter">{t("checkout.empty")}</h2>
        <p className="text-gray-500 mt-3">{t("checkout.empty.d")}</p>
        <Link to="/" className="mt-8 inline-block bg-gray-900 text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm hover:bg-gray-800">{t("checkout.empty.back")}</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: form,
        payment_method: method,
        promo_code: promoCode ? promoCode.code : null,
      };
      const { data: order } = await api.post("/orders", payload);

      if (method === "cod") {
        clear();
        toast.success(t("checkout.placed"));
        nav(`/comanda/${order.id}?cod=1`);
        return;
      }

      const origin = window.location.origin;
      const { data: ses } = await api.post("/checkout/session", { order_id: order.id, origin_url: origin });
      clear();
      window.location.href = ses.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("checkout.error"));
      setLoading(false);
    }
  };

  const FIELDS = [
    { k: "full_name", l: t("checkout.field.fullname"), w: "sm:col-span-2" },
    { k: "email", l: t("checkout.field.email"), t: "email" },
    { k: "phone", l: t("checkout.field.phone") },
    { k: "address", l: t("checkout.field.address"), w: "sm:col-span-2" },
    { k: "city", l: t("checkout.field.city") },
    { k: "county", l: t("checkout.field.county") },
    { k: "postal_code", l: t("checkout.field.postal") },
    { k: "notes", l: t("checkout.field.notes"), w: "sm:col-span-2", t: "text", required: false },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="checkout-page">
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">{t("checkout.title")}</h1>
      {cancelled && (
        <div className="mt-6 border border-amber-300 bg-amber-50 px-4 py-3 text-sm">{t("checkout.cancelled")}</div>
      )}

      <form onSubmit={submit} className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7 space-y-10">
          <section>
            <h2 className="font-display text-xl font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
              <span className="font-mono text-gray-400">01</span> {t("checkout.step1")}
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {FIELDS.map((f) => (
                <div key={f.k} className={f.w || ""}>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">{f.l}</label>
                  <input
                    type={f.t || "text"}
                    required={f.required !== false}
                    value={form[f.k]}
                    onChange={(e) => setF(f.k, e.target.value)}
                    className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-gray-900 focus:outline-none"
                    data-testid={`checkout-${f.k}`}
                  />
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold uppercase tracking-wide mb-6 flex items-center gap-2">
              <span className="font-mono text-gray-400">02</span> {t("checkout.step2")}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { v: "cod", title: t("checkout.cod"), desc: t("checkout.cod.d"), Icon: Truck },
                { v: "card", title: t("checkout.card"), desc: t("checkout.card.d"), Icon: CreditCard },
              ].map((opt) => (
                <label key={opt.v} className={`block cursor-pointer border p-5 transition ${method === opt.v ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-400'}`}>
                  <input type="radio" name="pm" value={opt.v} className="sr-only" checked={method === opt.v} onChange={() => setMethod(opt.v)} data-testid={`payment-${opt.v}`} />
                  <div className="flex items-start gap-3">
                    <opt.Icon size={22} strokeWidth={1.5} className="flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">{opt.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 border border-gray-200 bg-gray-50/60 p-6 md:p-8">
            <h3 className="font-display text-xl font-bold uppercase tracking-wide mb-5">{t("checkout.summary")}</h3>
            <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3 items-center">
                  <div className="w-14 h-14 bg-white border border-gray-200 flex-shrink-0 overflow-hidden">
                    <img src={i.image} alt="" className="w-full h-full object-contain p-1" />
                  </div>
                  <div className="flex-1 text-sm">
                    <p className="font-semibold leading-tight">{i.name}</p>
                    <p className="text-xs text-gray-500 font-mono">× {i.quantity}</p>
                  </div>
                  <span className="text-sm font-mono">{formatRON(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-gray-200 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">{t("cart.subtotal")}</span><span className="font-mono">{formatRON(grossSubtotal)}</span></div>
              {lineDiscountTotal > 0 && (
                <div className="flex justify-between text-[#188f42]" data-testid="checkout-line-discount">
                  <span className="inline-flex items-center gap-1.5"><Sparkles size={13} strokeWidth={1.5} /> {t("cart.disc.qty")}</span>
                  <span className="font-mono font-semibold">−{formatRON(lineDiscountTotal)}</span>
                </div>
              )}
              {promoCode ? (
                <div className="flex justify-between text-[#188f42]" data-testid="checkout-promo-applied">
                  <span className="inline-flex items-center gap-1.5"><Sparkles size={13} strokeWidth={1.5} /> {promoCode.code} ({promoCode.percent}%)</span>
                  <span className="font-mono font-semibold">−{formatRON(promoAmount)}</span>
                </div>
              ) : (
                discount.applied && (
                  <div className="flex justify-between text-[#188f42]" data-testid="checkout-discount">
                    <span className="inline-flex items-center gap-1.5"><Sparkles size={13} strokeWidth={1.5} /> {t("cart.disc.order")} ({discount.percent}%)</span>
                    <span className="font-mono font-semibold">−{formatRON(discount.amount)}</span>
                  </div>
                )
              )}
              <div className="flex justify-between"><span className="text-gray-500">{t("cart.shipping")}</span><span className="font-mono">{formatRON(SHIPPING)}</span></div>
              <div className="flex justify-between pt-3 border-t border-gray-200 mt-3">
                <span className="font-semibold uppercase tracking-wide text-sm">{t("checkout.total")}</span>
                <span className="font-display text-2xl font-black font-mono" data-testid="checkout-total">{formatRON(total)}</span>
              </div>
            </div>
            {!discount.applied && (
              <div className="mt-4 border border-dashed border-gray-300 bg-gray-50 px-3 py-2.5 text-[11px] text-gray-600 leading-relaxed" data-testid="checkout-discount-hint">
                {t("checkout.hint.addmore")} <span className="font-mono font-bold text-gray-900">{formatRON(discount.remaining)}</span> {t("checkout.hint.save")} <span className="font-bold text-[#1ebe57]">5%</span> {t("checkout.hint.atcheckout")}
              </div>
            )}
            <button type="submit" disabled={loading} className="mt-6 w-full bg-gray-900 text-white py-4 font-semibold uppercase tracking-wider text-sm hover:bg-gray-800 transition disabled:opacity-60" data-testid="place-order-button">
              {loading ? t("checkout.processing") : method === "card" ? t("checkout.submit.card") : t("checkout.submit.cod")}
            </button>

            <div className="mt-5 border-t border-gray-200 pt-5" data-testid="promo-code-section">
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">{t("checkout.promo")}</label>
              {promoCode ? (
                <div className="flex items-center justify-between border border-[#1ebe57] bg-[#1ebe57]/10 px-3 py-2.5">
                  <span className="text-sm font-bold font-mono text-[#188f42]">{promoCode.code}</span>
                  <button type="button" onClick={removePromo} className="text-xs uppercase tracking-wider text-gray-600 hover:text-red-600" data-testid="promo-remove">{t("checkout.promo.remove")}</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value); setPromoErr(""); }}
                    placeholder="ANZ…"
                    className="flex-1 border border-gray-300 bg-white px-3 py-2.5 text-sm font-mono uppercase focus:border-gray-900 focus:outline-none tracking-wider"
                    data-testid="promo-input"
                  />
                  <button type="button" onClick={applyPromo} disabled={promoChecking || !promoInput.trim()}
                    className="border border-gray-900 px-4 text-xs font-bold uppercase tracking-wider hover:bg-gray-900 hover:text-white transition disabled:opacity-50" data-testid="promo-apply">
                    {promoChecking ? "..." : t("checkout.promo.apply")}
                  </button>
                </div>
              )}
              {promoErr && <p className="mt-2 text-xs text-red-600" data-testid="promo-error">{promoErr}</p>}
            </div>
            <p className="mt-3 text-[11px] text-gray-500 leading-relaxed">{t("checkout.disclaimer")}</p>
          </div>
        </aside>
      </form>
    </div>
  );
}
